import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { mergeSessionRepresentative } from "@/features/auth/store/sessionSlice";
import { notifyTabAttention } from "@/shared/utils/tabAttention";
import type { AppDispatch } from "@/store";
import { CHAT_QUERY_KEYS } from "../configs/chat.config";
import { subscribeChannel, unsubscribeChannel } from "../services/chatPusher.service";
import type { PusherChatVisibilityEvent, PusherUnreadEvent } from "../types/chat.types";

/**
 * Request browser notification permission. Safe to call multiple times —
 * only prompts when permission is still 'default'.
 */
export function requestNotificationPermission() {
  if (!("Notification" in window)) return;
  if (Notification.permission === "default") {
    Notification.requestPermission();
  }
}

/**
 * Show a browser notification for an incoming chat message.
 * Only fires when the page is not focused (tab in background or minimised).
 */
function showBrowserNotification(data: PusherUnreadEvent) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (document.hasFocus()) return;

  const authorName = `${data.author.first_name} ${data.author.last_name}`;
  const title = data.chat_room_name || authorName;
  const body = `${authorName}: ${data.message_preview}`;

  const notification = new Notification(title, { body, tag: `chat-${data.chat_room_id}` });
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
}

/**
 * Subscribes to the personal notification channel for the current representative.
 * Receives chat_unread events even when the chat window is closed.
 * When the page is not focused, shows a browser notification with the message preview.
 * Also mirrors visibility changes made from another tab or device, which the
 * session of this tab would otherwise never learn about.
 */
export function useChatNotifications(
  representativeId: string | null,
  onUnread?: (data: PusherUnreadEvent) => void,
) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();

  const handleUnread = useCallback(
    (data: PusherUnreadEvent) => {
      queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEYS.rooms() });
      showBrowserNotification(data);
      notifyTabAttention();
      onUnread?.(data);
    },
    [queryClient, onUnread],
  );

  const handleVisibilityChanged = useCallback(
    (event: PusherChatVisibilityEvent) => {
      dispatch(mergeSessionRepresentative({ hide_chat: event.hide_chat }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!representativeId) return;

    const channelName = `private-representative_notifications_${representativeId}`;
    const channel = subscribeChannel(channelName);

    channel.bind("chat_unread", handleUnread);
    channel.bind("chat_visibility_changed", handleVisibilityChanged);

    return () => {
      channel.unbind("chat_unread", handleUnread);
      channel.unbind("chat_visibility_changed", handleVisibilityChanged);
      unsubscribeChannel(channelName);
    };
  }, [representativeId, handleUnread, handleVisibilityChanged]);
}
